// Admin User Management — TanStack Query mutation for
// POST /api/v1/admin/users/:id/block.
//
// Fresh Idempotency-Key per click (crypto.randomUUID). `until` null =
// PERMANENT block; an ISO timestamp = TIMED block that lifts then. The
// caller trims the reason before it reaches here.

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { attachRequestIdToScope } from "../../lib/api-client";

export type BlockUserInput = {
  until: string | null;
  reason: string;
};

export type BlockUserResponse = {
  data: {
    user_id: string;
    blocked_at: string;
    blocked_until: string | null;
    prior_creator_status: string;
    creator_status: string;
    sessions_revoked: number;
    cancelled_share_listings: number;
    cancelled_sealed_listings: number;
  };
};

export type BlockUserError = {
  code: string;
  message: string;
  field?: string;
  status: number;
};

async function blockUser(
  userId: string,
  input: BlockUserInput
): Promise<BlockUserResponse> {
  const response = await fetch(`/api/v1/admin/users/${userId}/block`, {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      "X-Idempotency-Key": crypto.randomUUID(),
    },
    body: JSON.stringify(input),
  });
  attachRequestIdToScope(response);
  if (!response.ok) {
    let detail: BlockUserError = {
      code: "ADMIN_BLOCK_FAILED",
      message: `Block failed (HTTP ${response.status})`,
      status: response.status,
    };
    try {
      const raw = await response.json();
      if (raw?.error) {
        detail = {
          code: raw.error.code,
          message: raw.error.message,
          field: raw.error.field,
          status: response.status,
        };
      }
    } catch {
      // body not JSON
    }
    throw detail;
  }
  return (await response.json()) as BlockUserResponse;
}

export function useBlockUser(userId: string) {
  const queryClient = useQueryClient();
  return useMutation<BlockUserResponse, BlockUserError, BlockUserInput>({
    mutationFn: (input) => blockUser(userId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-users"] });
      queryClient.invalidateQueries({
        queryKey: ["admin-user-detail", userId],
      });
    },
  });
}
